// Pure snapshot-diff danger detection for pause-on-danger (pacing spec §4 A2).
//
// Tracks every city the local player owns — not just the first, which is all
// useAttackToasts watches — and reports two kinds of danger between two
// snapshots:
//   - `captured` (major): a city we owned now belongs to someone else
//   - `attacked` (minor): a city we still own lost HP
// A per-(kind, city) cooldown in sim ticks keeps a long siege from pausing
// the game every few seconds.

import type { GameSnapshot } from '@aod/protocol';

export type DangerKind = 'captured' | 'attacked';
export type DangerSeverity = 'major' | 'minor';
export type PauseOnDanger = 'off' | 'major' | 'all';

export interface DangerAlert {
  readonly kind: DangerKind;
  readonly severity: DangerSeverity;
  readonly cityId: string;
  readonly cityName: string;
  readonly position: [number, number, number];
  /** HP lost since the previous snapshot (attacked only). */
  readonly damage?: number;
  /** New owner's player index (captured only). */
  readonly newOwner?: number;
}

export interface CityTrack {
  readonly ownerId: number;
  readonly hp: number;
}

export interface DangerState {
  /** Last seen state of every city, keyed by node id. */
  readonly cities: ReadonlyMap<string, CityTrack>;
  /** `${kind}:${cityId}` → tick the alert last fired. */
  readonly lastFired: ReadonlyMap<string, number>;
}

/** 30 s of game time at the default 10 ticks/s. */
export const DANGER_COOLDOWN_TICKS = 300;

export const EMPTY_DANGER_STATE: DangerState = { cities: new Map(), lastFired: new Map() };

const SEVERITY: Record<DangerKind, DangerSeverity> = { captured: 'major', attacked: 'minor' };

interface CityNode {
  id: string;
  name: string;
  ownerId: number;
  hp: number;
  position: [number, number, number];
}

function readCities(snapshot: GameSnapshot): CityNode[] {
  const out: CityNode[] = [];
  for (const n of snapshot.scene.children ?? []) {
    if (n.type !== 'city') continue;
    const ownerId = n.props?.ownerId;
    const hp = n.props?.hp;
    if (typeof ownerId !== 'number' || typeof hp !== 'number') continue;
    out.push({
      id: n.id,
      name: n.name ?? 'Your city',
      ownerId,
      hp,
      position: (n.transform?.position as [number, number, number] | undefined) ?? [0, 0, 0],
    });
  }
  return out;
}

/** Whether an alert of this severity should pause under the given setting. */
export function shouldPauseFor(mode: PauseOnDanger, severity: DangerSeverity): boolean {
  if (mode === 'off') return false;
  if (mode === 'all') return true;
  return severity === 'major';
}

/**
 * Diff `snapshot` against `state` and return the new alerts plus the next
 * state. The first call (empty state) only seeds tracking and never alerts,
 * so loading a save or starting a match can't trigger a pause.
 */
export function detectDangers(
  state: DangerState,
  snapshot: GameSnapshot | null,
  localPlayer: number,
  tick: number,
): { alerts: DangerAlert[]; next: DangerState } {
  if (!snapshot) return { alerts: [], next: state };

  const cities = readCities(snapshot);
  const seeded = state.cities.size > 0;
  const nextCities = new Map<string, CityTrack>();
  const lastFired = new Map(state.lastFired);
  const alerts: DangerAlert[] = [];

  const fire = (kind: DangerKind, city: CityNode, extra: Partial<DangerAlert>) => {
    const key = `${kind}:${city.id}`;
    const last = lastFired.get(key);
    if (last !== undefined && tick - last < DANGER_COOLDOWN_TICKS) return;
    lastFired.set(key, tick);
    alerts.push({
      kind,
      severity: SEVERITY[kind],
      cityId: city.id,
      cityName: city.name,
      position: city.position,
      ...extra,
    });
  };

  for (const city of cities) {
    nextCities.set(city.id, { ownerId: city.ownerId, hp: city.hp });
    const prev = state.cities.get(city.id);
    if (!seeded || !prev || prev.ownerId !== localPlayer) continue;

    if (city.ownerId !== localPlayer) {
      fire('captured', city, { newOwner: city.ownerId });
    } else if (city.hp < prev.hp) {
      fire('attacked', city, { damage: prev.hp - city.hp });
    }
  }

  return { alerts, next: { cities: nextCities, lastFired } };
}
